import { PageUrl } from '../../types/enums/pageUrl-enums';
import { RuntimeConfigurationValueType } from '../../types/enums/runtime-configuration-enums';

export const getRuntimeConfigurationVersionFromSearch = (search: string): number | null => {
    const raw = new URLSearchParams(search).get('version');
    if (!raw) {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildRuntimeConfigurationDetailUrl = (key: string, version?: number | null): string => {
    const base = `${PageUrl.runtimeConfigurationDetail.url}/${encodeURIComponent(key)}`;
    return version != null ? `${base}?version=${version}` : base;
};

export const isRuntimeConfigurationOperationFailed = (
    result: { success?: boolean; Success?: boolean } | null | undefined
): boolean => result?.success === false || result?.Success === false;

export const getRuntimeConfigurationOperationMessage = (
    result: { message?: string | null; Message?: string | null } | null | undefined,
    fallback: string
): string => {
    const message = result?.message ?? result?.Message;
    return message && message.trim().length > 0 ? message.trim() : fallback;
};

export const resolveRuntimeConfigurationMutationVersion = (
    result: { newVersion?: number; NewVersion?: number } | null | undefined
): number | null => {
    const version = result?.newVersion ?? result?.NewVersion;
    return version != null && Number.isFinite(version) ? version : null;
};

const VALUE_TYPE_BY_INDEX: RuntimeConfigurationValueType[] = [
    RuntimeConfigurationValueType.String,
    RuntimeConfigurationValueType.Boolean,
    RuntimeConfigurationValueType.Integer,
    RuntimeConfigurationValueType.Decimal,
    RuntimeConfigurationValueType.Date,
    RuntimeConfigurationValueType.DateTime,
    RuntimeConfigurationValueType.StringList,
    RuntimeConfigurationValueType.IntList,
    RuntimeConfigurationValueType.Json,
];

export const normalizeValueType = (valueType: unknown): RuntimeConfigurationValueType => {
    if (valueType === null || valueType === undefined || valueType === '') {
        return RuntimeConfigurationValueType.String;
    }
    if (typeof valueType === 'number' && Number.isInteger(valueType)) {
        return VALUE_TYPE_BY_INDEX[valueType] ?? RuntimeConfigurationValueType.String;
    }
    const text = String(valueType).trim();
    if (/^\d+$/.test(text)) {
        return VALUE_TYPE_BY_INDEX[Number(text)] ?? RuntimeConfigurationValueType.String;
    }
    return (text as RuntimeConfigurationValueType) || RuntimeConfigurationValueType.String;
};

export const getValueTypeTranslationKey = (valueType: unknown): string =>
    `runtimeConfiguration.valueTypes.${normalizeValueType(valueType)}`;

export const defaultValueForType = (valueType: RuntimeConfigurationValueType): unknown => {
    switch (valueType) {
        case RuntimeConfigurationValueType.Boolean:
            return false;
        case RuntimeConfigurationValueType.Json:
            return {};
        default:
            return '';
    }
};

export const isBooleanValueType = (valueType: RuntimeConfigurationValueType | string | number): boolean =>
    normalizeValueType(valueType) === RuntimeConfigurationValueType.Boolean;

export const isJsonValueType = (valueType: RuntimeConfigurationValueType | string | number): boolean =>
    normalizeValueType(valueType) === RuntimeConfigurationValueType.Json;

export type JsonValidationResult = {
    valid: boolean;
    error: string | null;
};

export const validateJSON = (jsonString: string): JsonValidationResult => {
    try {
        JSON.parse(jsonString);
        return { valid: true, error: null };
    } catch (error) {
        return {
            valid: false,
            error: error instanceof Error ? error.message : String(error),
        };
    }
};

export const isStringValueType = (valueType: RuntimeConfigurationValueType | string | number): boolean =>
    normalizeValueType(valueType) === RuntimeConfigurationValueType.String;

export const coerceBooleanValue = (value: unknown): boolean => value === true || value === 'true';

export const toPayloadValue = (value: unknown, valueType: RuntimeConfigurationValueType | string | number): unknown => {
    const normalizedType = normalizeValueType(valueType);
    if (isBooleanValueType(normalizedType)) {
        return coerceBooleanValue(value);
    }
    if (isJsonValueType(normalizedType)) {
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return value;
            }
        }
        return value ?? {};
    }
    return value == null ? '' : valueToDisplayString(value);
};

export const valueToDisplayString = (value: unknown): string => {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value);
};
